import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { List, ListItem, ListLabel } from '@apollo/orbit';
import {
  GraphQLEnumType,
  GraphQLInputObjectType,
  GraphQLInterfaceType,
  GraphQLNamedType,
  GraphQLObjectType,
  GraphQLScalarType,
  GraphQLSchema,
  GraphQLUnionType,
} from 'graphql';
import { TypeMap } from 'graphql/type/schema';
import moment from 'moment';
import React from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import {
  GraphQLArgumentNode,
  GraphQLNode,
  unwrapNestedType,
} from 'src/lib/graphql/graphqlHelpers';
import { GetIsEntity } from 'src/lib/graphql/subgraphMetadataHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { getStudioLinkFromEmbed } from 'src/lib/routers/embedded';

import { DescriptionMarkdownRenderer } from '../../documentationPage/schemaPage/descriptionMarkdownRenderer/DescriptionMarkdownRenderer';
import {
  Directive,
  getDirectiveKey,
} from '../../documentationPage/schemaPage/directive/Directive';
import { GraphRef } from '../../hooks/useGraphRef';
import { changelogRouteConfig, variantRouteConfig } from '../../routes';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import { isGraphqlField } from '../helpers/graphqlHelpers';
import { GraphQLPath } from '../helpers/operationPathHelpers';

import { Field } from './Field';
import { FieldSortingButton } from './FieldSortingButton';
import { FieldToggleType } from './getFieldToggle/getFieldToggle';
import { getAllCurrentScalars } from './getFieldToggle/getInsertion';
import { InputField } from './inputField/InputField';
import { PossibleType } from './PossibleType';
import { SelectAllButtonAndDropdown } from './SelectAllButtonAndDropdown';
import { SelectAllTypesButtonAndDropdown } from './SelectAllTypesButtonAndDropdown';
import { SortOrder, ToggleEditorContext, sortFields } from './shared';

const TypeMeta = ({
  node,
  typeMap,
}: {
  node: GraphQLNamedType;
  typeMap: TypeMap;
}) => {
  const directives = node.astNode?.directives;
  return (
    <>
      {(isMinLengthArray(1, directives) || !!node.description) && (
        <>
          <ListLabel className="mt-8">Metadata for {node.name} Type</ListLabel>
          {directives?.map((directive) => (
            <Directive
              directive={directive}
              typeMap={typeMap}
              key={getDirectiveKey(directive)}
            />
          ))}
          {!!node.description && (
            <div className="mt-2 text-sm text-secondary">
              <DescriptionMarkdownRenderer
                descriptionMarkdown={node.description}
                typeMap={typeMap}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export const NodeRenderer = React.memo(
  ({
    graphRef,
    schema,
    createdAt,
    hash,
    node,
    onSelectNode,
    fieldToggle,
    docsPath,
    argumentPath,
    parentFieldToggle,
    shouldManageVariables,
    toggleEditorContext,
    sortOrder,
    setSortOrder,
    explorerBehavioralFlags,
    typeMap,
    getIsEntity,
  }: {
    graphRef: GraphRef | null;
    schema: GraphQLSchema;
    createdAt: string | number;
    hash: string;
    node: GraphQLNode | null;
    onSelectNode: (node: GraphQLNode) => void;
    fieldToggle: FieldToggleType;
    docsPath: GraphQLPath;
    argumentPath: GraphQLArgumentNode[];
    parentFieldToggle?: Pick<
      NonNullable<FieldToggleType>,
      | 'getArgumentInOperation'
      | 'isArgumentPathValueSet'
      | 'toggleArgumentPathInOperation'
      | 'insertAllFieldsRecursively'
    >;
    shouldManageVariables: boolean;
    toggleEditorContext: ToggleEditorContext;
    sortOrder: SortOrder;
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;
    explorerBehavioralFlags: ExplorerBehavioralFlags;
    typeMap: TypeMap;
    getIsEntity: GetIsEntity | undefined;
  }) => {
    const location = useLocation();
    const isSelectAllScalarsDisabled = React.useMemo(
      () =>
        node && isGraphqlField(node) && getAllCurrentScalars(node).length === 0,
      [node],
    );

    if (!node) {
      return (
        <List isInteractive className="mt-8 !gap-0">
          <ListLabel className="leading-loose">Root Types</ListLabel>
          {[
            { type: schema.getQueryType(), label: 'query' },
            { type: schema.getMutationType(), label: 'mutation' },
            { type: schema.getSubscriptionType(), label: 'subscription' },
          ].map(({ type, label }) =>
            type ? (
              <Field
                key={label}
                isNodeInOperation={
                  fieldToggle?.isInOperation
                    ? () => fieldToggle.isInOperation(type)
                    : () => false
                }
                onSelect={() => onSelectNode(type)}
                toggleNodeInOperation={
                  fieldToggle?.toggleInOperation
                    ? () => fieldToggle.toggleInOperation(type)
                    : () => {}
                }
                field={{
                  name: label,
                  type,
                  description: '',
                  isDeprecated: false,
                  deprecationReason: '',
                  args: [],
                  extensions: undefined,
                }}
                getIsEntity={getIsEntity}
              />
            ) : null,
          )}
          {explorerBehavioralFlags.showSchemaVersion && graphRef && (
            <List isInteractive className="mt-6 !gap-0">
              <ListLabel className="leading-loose">Schema Version</ListLabel>
              <Tooltip label="Go to Schema Changelog" placement="top-end">
                <WrapMaybe
                  on={true}
                  element={
                    isEmbeddableExplorerRoute() ? (
                      <ListItem
                        as="a"
                        target="_blank"
                        href={getStudioLinkFromEmbed(
                          changelogRouteConfig.path({
                            ...graphRef,
                            // TODO: when we make embedded available to non public graphs, this should be updated
                            graphVisibilityType: 'public',
                          }),
                        )}
                        rel="noopener noreferrer"
                      />
                    ) : (
                      <ListItem
                        as={Link}
                        to={changelogRouteConfig.locationFrom({
                          location,
                          fromRouteConfig: variantRouteConfig,
                        })}
                      />
                    )
                  }
                >
                  <>
                    <div className="flex-1 text-sm font-semibold">
                      {moment(createdAt).format('MMMM Do, YYYY')}
                    </div>
                    <div className="flex-none font-mono text-sm">
                      {hash.substring(0, 6)}{' '}
                    </div>
                  </>
                </WrapMaybe>
              </Tooltip>
            </List>
          )}
        </List>
      );
    }

    const nestedType = 'type' in node ? unwrapNestedType(node.type) : node;

    if (nestedType instanceof GraphQLEnumType) {
      return (
        <List className="!gap-0">
          <TypeMeta node={nestedType} typeMap={typeMap} />
          <ListLabel className="mt-8 flex h-9 items-center">Values</ListLabel>
          {nestedType.getValues().map((enumValue) => (
            <Tooltip
              key={enumValue.name}
              placement="top"
              label={enumValue.description || ''}
              disabled={!enumValue.description}
            >
              <ListItem className="font-mono text-sm">
                {enumValue.name}
                {enumValue.astNode?.directives?.map((directive) => (
                  <Directive
                    directive={directive}
                    typeMap={typeMap}
                    key={getDirectiveKey(directive)}
                    className="ml-2 inline"
                  />
                ))}
                {enumValue.description && <IconInfo />}
              </ListItem>
            </Tooltip>
          ))}
        </List>
      );
    }

    if (nestedType instanceof GraphQLUnionType) {
      return (
        <List isInteractive className="!gap-0">
          <TypeMeta node={nestedType} typeMap={typeMap} />
          <ListLabel className="mt-8 flex items-center gap-2">
            Possible Types
            <SelectAllTypesButtonAndDropdown
              fieldToggle={fieldToggle}
              types={nestedType.getTypes()}
            />
          </ListLabel>
          {nestedType.getTypes().map((type) => (
            <PossibleType
              key={type.name}
              type={type}
              onSelect={onSelectNode}
              docsPath={docsPath}
              toggleEditorContext={toggleEditorContext}
              sortOrder={sortOrder}
              getIsEntity={getIsEntity}
            />
          ))}
        </List>
      );
    }

    if (nestedType instanceof GraphQLInterfaceType) {
      const implementations = schema.getImplementations(nestedType);
      return (
        <List isInteractive className="!gap-0">
          <TypeMeta node={nestedType} typeMap={typeMap} />
          <ListLabel className="mt-8 flex items-center gap-2">
            Fields
            <FieldSortingButton
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </ListLabel>
          {sortFields(sortOrder, Object.values(nestedType.getFields())).map(
            (field) => (
              <Field
                key={field.name}
                field={field}
                onSelect={onSelectNode}
                toggleNodeInOperation={fieldToggle?.toggleInOperation}
                isNodeInOperation={fieldToggle?.isInOperation}
                getIsEntity={getIsEntity}
              />
            ),
          )}
          <ListLabel className="mt-8 flex items-center gap-2">
            Implementations
            <SelectAllTypesButtonAndDropdown
              fieldToggle={fieldToggle}
              types={[
                ...implementations.objects,
                ...implementations.interfaces,
              ]}
            />
          </ListLabel>
          {[...implementations.objects, ...implementations.interfaces].map(
            (type) => (
              <PossibleType
                key={type.name}
                type={type}
                onSelect={onSelectNode}
                docsPath={docsPath}
                toggleEditorContext={toggleEditorContext}
                sortOrder={sortOrder}
                getIsEntity={getIsEntity}
              />
            ),
          )}
        </List>
      );
    }

    if (nestedType instanceof GraphQLObjectType && isGraphqlField(node)) {
      return (
        <List isInteractive className="!gap-0">
          {nestedType !== node && (
            <TypeMeta node={nestedType} typeMap={typeMap} />
          )}
          <ListLabel className="mt-8 flex items-center gap-2">
            Fields
            <FieldSortingButton
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
            <SelectAllButtonAndDropdown
              node={node}
              fieldToggle={fieldToggle}
              isSelectAllScalarsDisabled={isSelectAllScalarsDisabled}
            />
          </ListLabel>
          {sortFields(sortOrder, Object.values(nestedType.getFields())).map(
            (field) => (
              <Field
                key={field.name}
                field={field}
                onSelect={onSelectNode}
                toggleNodeInOperation={fieldToggle?.toggleInOperation}
                isNodeInOperation={fieldToggle?.isInOperation}
                insertAllFieldsRecursively={
                  fieldToggle?.insertAllFieldsRecursively
                }
                getIsEntity={getIsEntity}
              />
            ),
          )}
        </List>
      );
    }

    if (nestedType instanceof GraphQLInputObjectType) {
      const argument =
        isMinLengthArray(1, argumentPath) &&
        parentFieldToggle?.getArgumentInOperation(argumentPath[0]);
      return (
        <List isInteractive className="!gap-0">
          <TypeMeta node={nestedType} typeMap={typeMap} />
          <ListLabel className="mt-8 flex items-center gap-2">
            Input Fields
            <FieldSortingButton
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </ListLabel>
          {sortFields(sortOrder, Object.values(nestedType.getFields())).map(
            (field) => {
              return (
                <InputField
                  key={field.name}
                  inputField={field}
                  onSelect={onSelectNode}
                  shouldManageVariables={shouldManageVariables}
                  disableToggle={
                    !shouldManageVariables ||
                    (argument && !argument?.isVariable)
                  }
                  argumentPath={argumentPath}
                  isNodeInOperation={parentFieldToggle?.isArgumentPathValueSet}
                  toggleNodeInOperation={
                    parentFieldToggle?.toggleArgumentPathInOperation
                  }
                />
              );
            },
          )}
        </List>
      );
    }

    if (nestedType instanceof GraphQLScalarType) {
      return (
        <List className="!gap-0">
          <TypeMeta node={nestedType} typeMap={typeMap} />
        </List>
      );
    }

    return <div>unhandled type</div>;
  },
);
